import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import DatePicker from 'react-multi-date-picker';
import dayjs from 'dayjs';

import { APIcreateAssetGroup, APIupdateAssetGroup } from '../../../api/Assets';

import type { AssetGroup } from '../../../types/asset-group';

interface AssetGroupEditModalProps {
  assetGroup?: AssetGroup;
  show: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
};

const toPickerDate = (date: string | null) => (date ? dayjs(date.slice(0, 10)).toDate() : null);

const AssetGroupEditModal: React.FC<AssetGroupEditModalProps> = ({ assetGroup, show, onConfirm, onDismiss }) => {
  const [name, setName] = useState(assetGroup?.name ?? '');
  const [visibleFrom, setVisibleFrom] = useState(assetGroup?.visible_from ?? null);
  const [visibleTo, setVisibleTo] = useState(assetGroup?.visible_to ?? null);
  const dialogRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { mutate: save, isPending: saving, error } = useMutation({
    mutationFn: () => {
      const data = {
        name,
        visible_from: visibleFrom,
        visible_to: visibleTo,
      };
      return assetGroup ? APIupdateAssetGroup(assetGroup.id, data) : APIcreateAssetGroup(data);
    },
    onSuccess: onConfirm,
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleHide = (event: Event) => {
      if (event.target !== dialog) {
        return;
      }
      if (saving) {
        event.preventDefault();
      } else {
        onDismiss();
      }
    };
    dialog?.addEventListener('wa-hide', handleHide);
    return () => dialog?.removeEventListener('wa-hide', handleHide);
  }, [saving, onDismiss]);

  return (
    <wa-dialog ref={(dialog) => { dialogRef.current = dialog; }} label={assetGroup ? 'Edit image group' : 'New image group'} id="dialog-edit-asset-group" open={show}>
      <form
        ref={formRef}
        id="edit-asset-group-form"
        className="wa-stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!saving) {
            save();
          }
        }}
      >
        <div className="wa-stack wa-gap-xs">
          <label htmlFor="asset-group-name">Name</label>
          <input
            id="asset-group-name"
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            disabled={saving}
          />
        </div>
        <div className="wa-grid">
          <div className="wa-stack wa-gap-xs">
            <label htmlFor="asset-group-visible-from">Visible from</label>
            <DatePicker
              id="asset-group-visible-from"
              name="visible_from"
              className="asset-group-date-picker"
              containerClassName="asset-group-date-picker-container"
              inputClass="asset-group-date-input"
              format="MM/DD/YYYY"
              placeholder="mm/dd/yyyy"
              value={toPickerDate(visibleFrom)}
              maxDate={toPickerDate(visibleTo) ?? undefined}
              onChange={(date) => setVisibleFrom(date?.isValid ? date.format('YYYY-MM-DD') : null)}
              disabled={saving}
            />
            <wa-button className="asset-group-clear-date-button" size="s" appearance="plain" disabled={saving || !visibleFrom} onClick={() => setVisibleFrom(null)}>
              Clear start date
            </wa-button>
          </div>
          <div className="wa-stack wa-gap-xs">
            <label htmlFor="asset-group-visible-to">Visible to</label>
            <DatePicker
              id="asset-group-visible-to"
              name="visible_to"
              className="asset-group-date-picker"
              containerClassName="asset-group-date-picker-container"
              inputClass="asset-group-date-input"
              format="MM/DD/YYYY"
              placeholder="mm/dd/yyyy"
              value={toPickerDate(visibleTo)}
              minDate={toPickerDate(visibleFrom) ?? undefined}
              onChange={(date) => setVisibleTo(date?.isValid ? date.format('YYYY-MM-DD') : null)}
              disabled={saving}
            />
            <wa-button className="asset-group-clear-date-button" size="s" appearance="plain" disabled={saving || !visibleTo} onClick={() => setVisibleTo(null)}>
              Clear end date
            </wa-button>
          </div>
        </div>
        {error && <div className="error-message" role="alert">{error.message || 'Failed to save group. Please try again.'}</div>}
      </form>
      <wa-button slot="footer" size="s" appearance="outlined" disabled={saving} onClick={onDismiss}>
        Cancel
      </wa-button>
      <wa-button slot="footer" size="s" variant="brand" disabled={saving} onClick={() => formRef.current?.requestSubmit()}>
        {saving ? 'Saving...' : assetGroup ? 'Save changes' : 'Create group'}
      </wa-button>
    </wa-dialog>
  );
}

export default AssetGroupEditModal;
